import { faMessage } from '@fortawesome/pro-solid-svg-icons';

import { useTranslate } from '@growtherapy/i18n';
import { IconButtonProps, Tag } from '@growtherapy/sprout-ui';

import { ControlPanelButton, ControlPanelButtonTooltip, IconMenuItem } from '../../components';
import { DrawerButton, DrawerButtonProps } from '../../components/DrawerButton';
import { Translate } from '../../i18n/Translate';

export function ChatDrawerControlButton({
  drawerId,
  isChatDrawerOpen,
  messageCount = 0,
  loading,
  forDrawerStates,
  ...otherProps
}: {
  drawerId: string;
  isChatDrawerOpen: boolean;
  messageCount?: number;
} & Omit<IconButtonProps, 'aria-label' | 'icon' | 'iconName' | 'iconDefinition' | 'iconVariant' | 'children'> &
  Pick<DrawerButtonProps<IconButtonProps>, 'forDrawerStates'>) {
  const t = useTranslate();
  const label = isChatDrawerOpen
    ? t('telehealth-core:controls.closeChat')
    : t('telehealth-core:controls.openChatUnreadMessages', {
        count: messageCount,
      });

  return (
    <div className='hidden md:block relative'>
      <ControlPanelButtonTooltip childIsInteractive text={label}>
        <DrawerButton
          as={ControlPanelButton}
          forDrawerStates={forDrawerStates}
          aria-label={label}
          aria-expanded={isChatDrawerOpen}
          aria-controls={drawerId}
          iconDefinition={faMessage}
          loading={loading}
          disabled={loading}
          showVisualIndicator={messageCount > 0}
          {...otherProps}
        />
      </ControlPanelButtonTooltip>
    </div>
  );
}

export function ChatDrawerControlMenuItem({
  drawerId,
  isChatDrawerOpen,
  onClickChatButton,
  isChatButtonDisabled,
  unreadMessageCount,
}: {
  drawerId: string;
  isChatDrawerOpen: boolean;
  onClickChatButton: () => void;
  isChatButtonDisabled: boolean;
  unreadMessageCount?: number;
}) {
  const t = useTranslate();
  const count = unreadMessageCount ?? 0;

  return (
    <IconMenuItem
      aria-controls={drawerId}
      aria-expanded={isChatDrawerOpen}
      onClick={onClickChatButton}
      disabled={isChatButtonDisabled}
      icon={faMessage}
    >
      <span className='sr-only'>
        {isChatDrawerOpen ? (
          <Translate
            i18nKey='telehealth-core:controls.closeChatCountUnreadMessages'
            values={{ count }}
            components={{ 1: <span /> }}
          />
        ) : (
          <Translate
            i18nKey='telehealth-core:controls.openChatCountUnreadMessages'
            values={{ count }}
            components={{ 1: <span /> }}
          />
        )}
      </span>
      <span aria-hidden>{t('telehealth-core:controls.chat')}</span>
      {count > 0 && (
        <Tag use='lilac' aria-hidden>
          {count}
        </Tag>
      )}
    </IconMenuItem>
  );
}
